import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Download,
  HandCoins,
  Handshake,
  Info,
  Landmark,
  Loader2,
  MousePointer2,
  Network,
  Receipt,
  RefreshCw,
  ScrollText,
  Undo2,
  UserMinus,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import PrivateLayout from '../../layouts/PrivateLayout';
import { useUser } from '../../../contexts/UserContext';
import { money, fmtDate } from '../shared/billingFormat';
import ConfirmModal from '../shared/ConfirmModal';
import { buildCsv, downloadTextFile } from '../../../lib/csv';
import { buildEnvoyLedgerLines, LEDGER_CSV_HEADER } from './ledgerCsv';
import { rangeLabel } from '../../../lib/dateRange';
import { DateRangePicker } from '../../operations/onboardingSupport/DateRangeCalendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
  useEnvoyDashboard,
  useEnvoyConnections,
  fetchEnvoyExport,
  useMarkCommissionPaid,
  useUnmarkCommissionPaid,
  useMarkCommissionRecouped,
  useUnmarkCommissionRecouped,
} from '../../../services/envoyFinance';

// ---------------------------------------------------------------------------
// Design language: the Lead Metrics > Cadence scorecard (MetricsPage.js).
// Off-white page (#F5F7FA via PrivateLayout background), white rounded-2xl
// cards with the shadow-as-border recipe (never a CSS border), a dark hero
// scorecard with soft glow blobs, short flip KPI tiles, and a pure-SVG
// donut. Deliberately light-committed, same as the Cadence tab.
// ---------------------------------------------------------------------------

const n = (v) => Number(v || 0).toLocaleString('en-US');

const CARD_SHADOW =
  'shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04),0_8px_16px_-8px_rgba(15,23,42,0.06)]';

const COLORS = {
  owed: '#F59E0B', // amber-500
  paid: '#10B981', // emerald-500
  recoup: '#EF4444', // red-500
  window: '#38BDF8', // sky-400 (Connections ring)
  restDark: 'rgba(255,255,255,0.12)',
};

// The hero palette is tuned for the dark card; on the white tiles the backs
// reuse the four icon hues instead (Nic 2026-09-02): orange, emerald, rose,
// sky, with slate for void.
const TILE_COLORS = {
  owed: '#F97316', // orange-500
  paid: '#10B981', // emerald-500
  recoup: '#F43F5E', // rose-500
  recovered: '#0EA5E9', // sky-500
  void: '#94A3B8', // slate-400
};

// Instant hover tooltip (no native-title delay): a muted icon that flips
// to slate-700 under the cursor with a dark label above it.
// `wide` = a wrapping paragraph bubble (tile explainers, tombstone history)
// instead of the one-line pill. `hoverClass` = the icon's hover color.
const HoverTip = ({
  label,
  children,
  wide = false,
  hoverClass = 'text-slate-700',
  align = 'center',
  ariaLabel,
}) => {
  const [show, setShow] = useState(false);
  const text = ariaLabel || (typeof label === 'string' ? label : undefined);
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 cursor-default',
        show ? hoverClass : 'text-slate-400',
      )}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow((v) => !v)}
      aria-label={text}
    >
      {children}
      {show ? (
        <span
          role="tooltip"
          className={cn(
            'pointer-events-none absolute bottom-full z-40 mb-1.5 rounded-md bg-slate-900 text-white shadow-lg',
            wide
              ? 'w-64 px-3 py-2 text-left text-[11px] font-normal leading-4'
              : 'whitespace-nowrap px-2 py-1 text-[11px] font-medium',
            align === 'right'
              ? 'right-0'
              : align === 'left'
                ? 'left-0'
                : 'left-1/2 -translate-x-1/2',
          )}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
};

// A tiny tombstone (Lucide has none): rounded-top stone with a cross. Marks
// a ledger row whose client went to DEAD and came back (Nic 2026-09-02).
const Tombstone = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 21V10a6 6 0 0 1 12 0v11" />
    <path d="M4 21h16" />
    <path d="M12 8v6" />
    <path d="M9.5 11h5" />
  </svg>
);

// A client who went to DEAD and came back (one entry per closed cycle in
// meta.history): a tombstone that turns violet on hover and lists the
// cycles, newest first. Null for everyone else.
const tombstoneFor = (row) => {
  const history = Array.isArray(row.meta?.history) ? row.meta.history : [];
  if (!history.length) return null;
  const cycles = [...history].reverse();
  const label = (
    <span className="block space-y-1">
      {cycles.map((h, i) => (
        <span key={i} className="block">
          Went to DEAD {fmtDate(h.withdrawal?.at || h.voidedAt || h.recoupFlaggedAt)} · back in
          the pipeline {fmtDate(h.restoredAt)}
          {h.recoupedAt
            ? ` · $${Number(row.sa?.amount || 250).toLocaleString('en-US')} recovered from Envoy ${fmtDate(h.recoupedAt)}`
            : h.paidAt
              ? ' · the $250 had been paid to Envoy'
              : ''}
        </span>
      ))}
      {row.sa?.status === 'owed' && row.sa?.owedAt ? (
        <span className="block text-slate-300">
          Owed again since {fmtDate(row.sa.owedAt)}
        </span>
      ) : null}
    </span>
  );
  return (
    <HoverTip label={label} wide align="left" hoverClass="text-violet-700">
      <Tombstone size={14} />
    </HoverTip>
  );
};

// The VA.gov rule (Nic 2026-10-25): agreements signed from its first day
// are owed from the day their onboarding specialist confirmed VA.gov (a
// row only exists once they have). A small emerald landmark on those rows;
// the hover says who confirmed it and when, the day that sets its month.
const vaGovMarkFor = (row) => {
  const va = row.vaGov;
  if (!va) return null;
  const label = `VA.gov confirmed by ${va.byName || 'their onboarding specialist'} ${fmtDate(va.at)}. The $250 is owed from that day.`;
  return (
    <HoverTip label={label} wide align="left" ariaLabel={label} hoverClass="text-emerald-700">
      <Landmark size={13} strokeWidth={2} className="text-emerald-500" aria-hidden />
    </HoverTip>
  );
};

// `center` (optional) renders a third header slot dead center between the
// title and `right` (1fr / auto / 1fr, the page-header recipe); below md the
// three stack, centered.
const Card = ({ title, caption, right, center, children, className = '' }) => (
  <div className={`relative rounded-2xl bg-white p-5 ${CARD_SHADOW} ${className}`}>
    {title || right || center ? (
      <div
        className={cn(
          'mb-4 gap-x-3 gap-y-2',
          center
            ? 'grid grid-cols-1 items-center justify-items-center md:grid-cols-[1fr_auto_1fr] md:justify-items-stretch'
            : 'flex flex-wrap items-baseline justify-between',
        )}
      >
        <span className="min-w-0 text-xs font-semibold uppercase tracking-wider text-slate-400">
          {title}
          {caption ? (
            <span className="ml-2 normal-case tracking-normal text-slate-300">
              {caption}
            </span>
          ) : null}
        </span>
        {center ? <span className="justify-self-center">{center}</span> : null}
        {center ? (
          // Wraps (never slides under the tabs) when the pills outgrow their
          // column.
          <span className="flex min-w-0 flex-wrap items-center justify-center gap-x-2 gap-y-1 md:justify-end">
            {right}
          </span>
        ) : (
          right
        )}
      </div>
    ) : null}
    {children}
  </div>
);

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse rounded-2xl bg-slate-200/60 ${className}`} />
);

// Pure-SVG donut (MetricsPage recipe): segments clockwise from 12 o'clock,
// center free for content.
const Donut = ({ segments, size = 132, stroke = 13, track, children }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((n, s) => n + s.value, 0);
  let acc = 0;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={track || '#E2E8F0'}
          strokeWidth={stroke}
        />
        {total > 0
          ? segments
              .filter((s) => s.value > 0)
              .map((s) => {
                const frac = s.value / total;
                const dash = `${frac * c} ${c}`;
                const offset = -acc * c;
                acc += frac;
                return (
                  <circle
                    key={s.key}
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={stroke}
                    strokeDasharray={dash}
                    strokeDashoffset={offset}
                    strokeLinecap="butt"
                  />
                );
              })
          : null}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
};

// Dark hero scorecard shell with the glow blobs. `hint` renders pinned to the
// card's bottom-right corner (the flip affordance).
const HeroCard = ({ children, hint }) => (
  <div className="relative h-full overflow-hidden rounded-2xl bg-[#0E121B] p-6 shadow-[0_8px_30px_rgba(15,23,42,0.25)]">
    <div
      aria-hidden
      className="pointer-events-none absolute -left-16 -top-24 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl"
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full bg-sky-500/15 blur-3xl"
    />
    <div className="relative h-full">{children}</div>
    {hint}
  </div>
);

// Click-to-flip card (Cadence scorecard recipe): pure CSS 3D, each face
// rotates independently (the shared preserve-3d parent pattern is what
// Chrome's compositor intermittently drops). The FRONT drives the height;
// the back is a full-cover overlay and must FIT that height.
// `disabled` = inert (a dimmed tile does not flip); `resetKey` = flips back
// to the front whenever it changes (the window filter, Nic 2026-09-02).
const FlipCard = ({ front, back, className = '', disabled = false, resetKey = null }) => {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    setFlipped(false);
  }, [resetKey]);
  const toggle = () => {
    if (!disabled) setFlipped((v) => !v);
  };
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      className={`${disabled ? 'cursor-default' : 'cursor-pointer'} select-none outline-none ${className}`}
    >
      <div className="relative h-full [perspective:1400px]">
        <div
          className={`h-full transition-transform duration-500 [backface-visibility:hidden] [will-change:transform] ${
            flipped ? '[transform:rotateY(180deg)]' : '[transform:rotateY(0deg)]'
          }`}
        >
          {front}
        </div>
        <div
          className={`absolute inset-0 overflow-hidden rounded-2xl transition-transform duration-500 [backface-visibility:hidden] [will-change:transform] ${
            flipped ? '[transform:rotateY(0deg)]' : '[transform:rotateY(-180deg)]'
          }`}
        >
          {back}
        </div>
      </div>
    </div>
  );
};

// Flip affordance: bare pointing-finger icon bottom right (X on detail faces).
const FlipHint = ({ close = false }) => (
  <span
    aria-hidden
    className="pointer-events-none absolute bottom-3.5 right-3.5 text-slate-500"
  >
    {close ? (
      <X size={17} strokeWidth={2.4} />
    ) : (
      <MousePointer2 size={17} strokeWidth={2.4} />
    )}
  </span>
);

// Cadence back-face recipe: bright slate-300 label, white bold value.
const LegendChip = ({ color, value, label }) => (
  <span className="inline-flex items-center gap-2.5 text-base text-slate-300">
    <span
      className="h-3 w-3 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
    <b className="text-2xl tabular-nums text-white">{value}</b>
    {label}
  </span>
);

// KPI tile = a short flip card (Nic 2026-09-02). Front: icon, label, the
// number. Back: a one-line context and the breakdown as colored numbers
// (`stats` = [{ label, value, color }]), laid out to fit the same short
// rectangle. Replaced the footer captions and then an "i" tooltip: the
// breakdown deserves a layout, not a paragraph.
const TILE_H = 'h-[8.75rem]';

// Back-face stat: the number carries the color, the label follows on the
// same line. Never truncates; the row simply wraps.
const KpiStat = ({ label, value, color }) => (
  <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap text-[11px] text-slate-500">
    <b className="text-sm font-semibold tabular-nums leading-none" style={{ color }}>
      {value}
    </b>
    {label}
  </span>
);

// `info` = the plain-English explainer, behind a small "i" on the FRONT
// (hover); the back is numbers only so it breathes.
// `muted` (a window is active and the row is dimmed): the "i" stays visible
// but inert, so no half-faded bubble can open over the dimmed tile. A click
// anywhere, the "i" included, still flips the tile.
// `accent` = the text color the back's title takes (the icon's hue).
const KpiCard = ({
  icon: Icon,
  tint,
  accent = 'text-slate-500',
  label,
  value,
  info,
  stats = [],
  muted = false,
  flipKey = null,
}) => {
  // Thousands separators, and the font steps down as the number grows so a
  // five-figure count still fits the tile.
  const display =
    typeof value === 'number' ? value.toLocaleString('en-US') : String(value ?? '');
  const sizeCls =
    display.length > 6 ? 'text-3xl' : display.length > 4 ? 'text-4xl' : 'text-[2.75rem]';
  const front = (
    <div className={`relative flex ${TILE_H} flex-col rounded-2xl bg-white p-4 ${CARD_SHADOW}`}>
      <div className="flex items-center gap-2.5 pr-5">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tint}`}>
          <Icon size={16} strokeWidth={2} />
        </span>
        <span className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-slate-400">
          {label}
        </span>
      </div>
      {info ? (
        <span className="absolute right-3 top-3">
          {muted ? (
            <Info size={13} strokeWidth={2} className="text-slate-300" />
          ) : (
            <HoverTip label={info} wide align="right">
              <Info size={13} strokeWidth={2} className="text-slate-300 transition-colors hover:text-slate-600" />
            </HoverTip>
          )}
        </span>
      ) : null}
      <div className="flex flex-1 items-center justify-center">
        <span
          className={`${sizeCls} font-semibold leading-none tracking-tight tabular-nums text-slate-900`}
        >
          {display}
        </span>
      </div>
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-3.5 right-3.5 text-slate-300"
      >
        <MousePointer2 size={13} strokeWidth={2.4} />
      </span>
    </div>
  );
  // Back: the same header as the front (the tile keeps its identity when it
  // turns), a thin bar showing the actual split in the stat colors, and the
  // colored numbers as that bar's legend.
  const total = stats.reduce((s, x) => s + (Number(x.raw) || 0), 0);
  const back = (
    <div className={`relative flex ${TILE_H} flex-col rounded-2xl bg-white p-4 ${CARD_SHADOW}`}>
      <div
        className={cn(
          'shrink-0 pr-5 text-[11px] font-semibold uppercase leading-tight tracking-wider',
          accent,
        )}
      >
        {label}
      </div>
      {/* shrink-0: in a fixed-height column the bar must never be the thing
          that gives way when the legend wraps to a second line. */}
      <div className="mt-4 flex h-2 w-full shrink-0 overflow-hidden rounded-full bg-slate-100">
        {total > 0
          ? stats
              .filter((s) => Number(s.raw) > 0)
              .map((s) => (
                // Proportional via flex-grow, with a floor so a 1-of-144
                // sliver still reads as a segment, not a speck. No hairline
                // gaps: the colors meet edge to edge.
                <span
                  key={s.label}
                  className="h-full"
                  style={{
                    flex: `${Number(s.raw)} 1 0%`,
                    minWidth: 8,
                    backgroundColor: s.color,
                  }}
                />
              ))
          : null}
      </div>
      {/* The legend owns the rest of the tile and centers in it, rows and
          all, so two lines sit as deliberately as one. */}
      <div className="flex flex-1 flex-wrap content-center items-center justify-center gap-x-5 gap-y-1.5 pt-2">
        {stats.map((s) => (
          <KpiStat key={s.label} {...s} />
        ))}
      </div>
      <span
        aria-hidden
        className="pointer-events-none absolute right-3.5 top-3.5 text-slate-300"
      >
        <X size={13} strokeWidth={2.4} />
      </span>
    </div>
  );
  return <FlipCard front={front} back={back} disabled={muted} resetKey={flipKey} />;
};

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'rounded-lg px-4 py-1.5 text-sm font-medium transition-colors',
      active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-slate-500 hover:text-slate-800',
    )}
  >
    {children}
  </button>
);

// Status pill per commission leg.
const LEG_PILL = {
  owed: { label: 'Owed', cls: 'bg-blue-50 text-blue-600' },
  void: { label: 'Void', cls: 'bg-slate-100 text-slate-400' },
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-600' },
  recoup: { label: 'Recoup', cls: 'bg-red-50 text-red-600' },
  recouped: { label: 'Recouped', cls: 'bg-slate-100 text-slate-500' },
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-400' },
};

const LegPill = ({ status }) => {
  const p = LEG_PILL[status] || LEG_PILL.pending;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.cls}`}
    >
      {p.label}
    </span>
  );
};

// State and action as ONE control (Nic 2026-09-02): the tinted status half
// on the left, the action half on the right, joined inside one squared
// capsule with a hairline between them, so "Mark paid" can only ever read
// as "mark THIS owed amount paid". Only the action half is a button;
// hovering "Owed" changes nothing.
const ActionPill = ({ status, action, onClick }) => {
  const p = LEG_PILL[status] || LEG_PILL.pending;
  return (
    <span className="inline-flex items-stretch overflow-hidden whitespace-nowrap rounded-lg bg-white text-[11px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/80">
      <span className={`inline-flex items-center px-2.5 py-1 ${p.cls}`}>{p.label}</span>
      <button
        type="button"
        onClick={onClick}
        className="border-l border-slate-200/80 px-2.5 py-1 text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white focus-visible:border-slate-900 focus-visible:bg-slate-900 focus-visible:text-white focus-visible:outline-none"
      >
        {action}
      </button>
    </span>
  );
};

// Rows rendered per page of a list; "Show more" adds another page.
const PAGE_SIZE = 25;

// Segmented view switch inside a card header: a soft slate track, the
// page's dark pill for the active view (white count badge on it), near-white
// count badges elsewhere that turn emerald when rows just moved in, and a
// rose dot when a view needs attention (Nic 2026-09-02).
const ViewTabs = ({ value, onChange, options }) => (
  <span className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100/80 p-1 ring-1 ring-inset ring-slate-200/60">
    {options.map((o) => {
      const active = value === o.key;
      return (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all',
            active
              ? 'bg-slate-900 text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-800',
          )}
        >
          {o.alert ? (
            <span className={cn('h-1.5 w-1.5 rounded-full', active ? 'bg-rose-400' : 'bg-rose-500')} />
          ) : null}
          {o.label}
          {o.count != null ? (
            // The count badge. Rows that just moved INTO this view from an
            // action elsewhere turn the badge emerald until the view is
            // opened; nothing is added, so the tabs never shift.
            <span
              className={cn(
                'rounded-md px-1.5 text-[10px] font-semibold tabular-nums leading-4 transition-colors',
                o.moved
                  ? 'bg-emerald-500 text-white'
                  : active
                    ? 'bg-white text-slate-900'
                    : 'bg-white/80 text-slate-500 ring-1 ring-inset ring-slate-200/70',
              )}
            >
              {o.count}
            </span>
          ) : null}
        </button>
      );
    })}
  </span>
);

const Th = ({ children, className = '' }) => (
  <th
    className={`truncate px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 ${className}`}
  >
    {children}
  </th>
);

// ---------------------------------------------------------------------------
// Ledger header controls: export + the window. Year is the home state (Nic
// 2026-10-25: "All time" went, like the other metrics pages); Month is a
// one-click month + year picker (Nic 2026-09-02:
// "easily jump to a month", the shape Envoy's statements come in); Custom
// opens the Cadence tab's two-month range picker. Rendered in the page
// header to the right of the tab bar so tab swaps cause no layout shift;
// shares the dashboard query with LedgerTab through the query cache.
// ---------------------------------------------------------------------------

// `range` = { start, end } (YYYY-MM-DD, Central).
const rangeText = (range) => {
  if (!range) return '';
  const y = wholeYearOf(range);
  return y ? String(y) : rangeLabel(range.start, range.end);
};

// A whole calendar year (the page's home state since 2026-10-25).
export const yearRange = (y) => ({ start: `${y}-01-01`, end: `${y}-12-31` });
const wholeYearOf = (range) => {
  if (!range) return null;
  const m = /^(\d{4})-01-01$/.exec(range.start || '');
  return m && range.end === `${m[1]}-12-31` ? Number(m[1]) : null;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const monthRange = (y, m) => {
  const start = dayjs(new Date(y, m, 1));
  return {
    start: start.format('YYYY-MM-DD'),
    end: start.endOf('month').format('YYYY-MM-DD'),
  };
};

// A range that is exactly one calendar month renders as "Jul 2026" and
// lights the Month pill; anything else is Custom.
const wholeMonthOf = (range) => {
  if (!range) return null;
  const start = dayjs(range.start, 'YYYY-MM-DD');
  if (!start.isValid() || start.date() !== 1) return null;
  if (start.endOf('month').format('YYYY-MM-DD') !== range.end) return null;
  return { y: start.year(), m: start.month() };
};

// The month grid only (2026-10-25): it sits in a Popover, which places it,
// keeps it on screen and handles Escape and click-outside.
const MonthPicker = ({ value, since, onCommit }) => {
  const today = dayjs();
  const minYear = since ? dayjs(since).year() : today.year();
  const [year, setYear] = useState(value?.y ?? today.year());

  const sinceKey = since ? dayjs(since).format('YYYY-MM') : null;
  const disabledMonth = (m) => {
    const key = dayjs(new Date(year, m, 1)).format('YYYY-MM');
    if (key > today.format('YYYY-MM')) return true;
    return Boolean(sinceKey && key < sinceKey);
  };

  return (
    <div className="w-56">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          disabled={year <= minYear}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label="Previous year"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold tabular-nums text-slate-800">{year}</span>
        <button
          type="button"
          onClick={() => setYear((y) => y + 1)}
          disabled={year >= today.year()}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label="Next year"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {MONTHS.map((label, m) => {
          const selected = value && value.y === year && value.m === m;
          const disabled = disabledMonth(m);
          return (
            <button
              key={label}
              type="button"
              disabled={disabled}
              onClick={() => onCommit(monthRange(year, m))}
              className={cn(
                'rounded-lg py-1.5 text-xs font-semibold transition-colors',
                selected
                  ? 'bg-slate-900 text-white shadow-sm'
                  : disabled
                    ? 'text-slate-300'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LedgerControls = ({ range, setRange }) => {
  const { data, isLoading, isFetching } = useEnvoyDashboard(range);
  const [exporting, setExporting] = useState(false);
  // Which popover is open: 'month' | 'custom' | null.
  const [open, setOpen] = useState(null);

  const month = wholeMonthOf(range);
  const year = wholeYearOf(range);
  const isCustom = Boolean(range) && !month && !year;
  const firstYear = data?.firstSignedAt ? dayjs(data.firstSignedAt).year() : dayjs().year();
  const years = [];
  for (let y = dayjs().year(); y >= firstYear; y -= 1) years.push(y);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Pull every client, then keep lines by their OWN date: a Dead or
      // Client-paid line belongs to the month it happened, not the month the
      // client signed (the ledger view scopes by signing date instead).
      const res = await fetchEnvoyExport(null);
      const csvRows = buildEnvoyLedgerLines(res?.rows || [], range);
      const stamp = range ? `${range.start}_${range.end}` : dayjs().format('YYYY-MM-DD');
      downloadTextFile({
        content: buildCsv(LEDGER_CSV_HEADER, csvRows),
        filename: `envoy-ledger-${stamp}.csv`,
        type: 'text/csv;charset=utf-8',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <span className="flex w-3.5 shrink-0 items-center justify-center">
        <RefreshCw
          className={cn(
            'h-3.5 w-3.5 animate-spin text-slate-300',
            isFetching && !isLoading ? 'visible' : 'invisible',
          )}
        />
      </span>
      <button
        type="button"
        onClick={handleExport}
        // The export keeps lines by their own date and pulls every client,
        // so it can have rows in a window where nobody signed (a Dead or a
        // Client paid line). Gate on "any client exists", not on the windowed
        // ledger.
        disabled={exporting || isLoading || !data?.firstSignedAt}
        title="Export CSV"
        aria-label="Export CSV"
        className={`inline-flex items-center gap-1.5 rounded-xl bg-white px-2.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50 lg:px-3 lg:py-1.5 ${CARD_SHADOW}`}
      >
        {exporting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        <span className="hidden lg:inline">Export CSV</span>
      </button>
      <div className={`flex items-center gap-0.5 rounded-xl bg-white p-1 ${CARD_SHADOW}`}>
        <Popover open={open === 'year'} onOpenChange={(v) => setOpen(v ? 'year' : null)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1 text-xs font-semibold transition-colors',
                year ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800',
              )}
            >
              {year ? String(year) : 'Year'}
            </button>
          </PopoverTrigger>
          <PopoverContent aria-label="Pick a year">
            <div className="grid w-40 grid-cols-2 gap-1">
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    setOpen(null);
                    setRange(yearRange(y));
                  }}
                  className={cn(
                    'rounded-lg py-1.5 text-xs font-semibold tabular-nums transition-colors',
                    y === year ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  )}
                >
                  {y}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <Popover open={open === 'month'} onOpenChange={(v) => setOpen(v ? 'month' : null)}>
          <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1 text-xs font-semibold transition-colors',
              month
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800',
            )}
          >
            {month ? `${MONTHS[month.m]} ${month.y}` : 'Month'}
          </button>
          </PopoverTrigger>
          <PopoverContent aria-label="Pick a month">
            <MonthPicker
              value={month}
              since={data?.firstSignedAt}
              onCommit={(next) => {
                setOpen(null);
                setRange(next);
              }}
            />
          </PopoverContent>
        </Popover>
        <DateRangePicker
          open={open === 'custom'}
          onOpenChange={(v) => setOpen(v ? 'custom' : null)}
          value={range || null}
          onCommit={({ start, end }) => setRange({ start, end })}
        >
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1 text-xs font-semibold transition-colors',
              isCustom
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800',
            )}
          >
            <CalendarDays size={13} />
            {isCustom ? rangeText(range) : 'Custom'}
          </button>
        </DateRangePicker>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Ledger tab
// ---------------------------------------------------------------------------

const LedgerTab = ({ canManage, range }) => {
  const { data, isLoading } = useEnvoyDashboard(range);
  const rangeKey = range ? `${range.start}|${range.end}` : 'all';

  const [action, setAction] = useState(null);
  const [reference, setReference] = useState('');
  const markPaid = useMarkCommissionPaid();
  const unmarkPaid = useUnmarkCommissionPaid();
  const markRecouped = useMarkCommissionRecouped();
  const unmarkRecouped = useUnmarkCommissionRecouped();

  const monthLabel = rangeText(range);

  const tiles = data?.tiles;
  const summary = data?.moneySummary;
  const ledger = data?.ledger || [];
  const needsReview = data?.needsReview || [];

  // Ledger views (Nic 2026-09-02): Owed / Paid / Recoup / All in one card.
  // Recoup = the $250s we paid Envoy for clients who then went to DEAD before
  // paying us: open on the left, recovered on the right. Rows page in
  // chunks so a long ledger never renders hundreds of rows at once.
  // Owed first: the to-do list is what Finance opens the page for.
  const [view, setView] = useState('owed');
  const [shown, setShown] = useState(PAGE_SIZE);
  // Owed follows the window like every other view (Nic 2026-11-03: the
  // every-period Owed from the 2026-10-26 QA batch was never intended). An
  // old unpaid leg stays visible through the hero's "from other months" line.
  const owedRows =
    data?.owedLedger ||
    ledger.filter((r) => r.sa.status === 'owed' || r.pay.status === 'owed');
  const owedNowAmount = data?.owedNow ? data.owedNow.amount : summary?.owedNowAmount || 0;
  const owedElsewhere = data?.owedElsewhere || { count: 0, amount: 0 };
  const paidRows = ledger.filter(
    (r) => r.sa.status === 'paid' || r.pay.status === 'paid',
  );
  // Buckets in the order they are worked: oldest death first on the left
  // (the ones to chase), newest recovery first on the right (the one you
  // just marked lands on top).
  const at = (v) => (v ? new Date(v).getTime() : 0);
  const recoupOpen = ledger
    .filter((r) => r.sa.status === 'recoup')
    .sort(
      (a, b) =>
        at(a.meta?.withdrawal?.at || a.sa.recoupFlaggedAt) -
        at(b.meta?.withdrawal?.at || b.sa.recoupFlaggedAt),
    );
  const recovered = ledger
    .filter((r) => r.sa.status === 'recouped')
    .sort((a, b) => at(b.sa.recoupedAt) - at(a.sa.recoupedAt));
  const rowsOf = (v) =>
    v === 'owed' ? owedRows : v === 'paid' ? paidRows : ledger;
  const viewRows = rowsOf(view);
  const visibleRows = viewRows.slice(0, shown);

  // Moved rows (Nic 2026-09-02): an action that sends a row to ANOTHER view
  // puts a count badge on that view's tab; the first time that view opens,
  // the rows pulse and the list scrolls to the first one (paging out far
  // enough to show it). An action that keeps the row in the current view
  // (Mark recovered: left panel to right panel) pulses it right away.
  const [pending, setPending] = useState({}); // view -> [commissionId]
  const [flash, setFlash] = useState([]);
  // Bucket moves color their pulse: emerald into Recovered, rose back into
  // To get back (Nic 2026-09-02). Null = the ledger table's default.
  const [flashTone, setFlashTone] = useState(null);
  // Beats per pulse: 2 for rows moved by an action, 3 when the user jumped
  // to a row on purpose (a Recoup / Recouped pill in the table).
  const [flashBeats, setFlashBeats] = useState(2);
  const flashNeed = useRef(0);
  const jumpToRecoup = (row) => {
    // Page the target bucket out far enough to include the card (the view
    // change resets paging to the first page otherwise).
    const list = row.sa.status === 'recoup' ? recoupOpen : recovered;
    const idx = list.findIndex((r) => r.commissionId === row.commissionId);
    flashNeed.current = idx >= 0 ? Math.ceil((idx + 1) / PAGE_SIZE) * PAGE_SIZE : 0;
    setFlashTone(row.sa.status === 'recoup' ? 'rose' : 'emerald');
    setFlashBeats(3);
    setFlash([row.commissionId]);
    setView('recoup');
  };
  const changeView = (next) => {
    const ids = pending[next] || [];
    if (ids.length) {
      const rows = rowsOf(next);
      const maxIdx = Math.max(...ids.map((id) => rows.findIndex((r) => r.commissionId === id)));
      flashNeed.current = maxIdx >= 0 ? Math.ceil((maxIdx + 1) / PAGE_SIZE) * PAGE_SIZE : 0;
      setPending((p) => ({ ...p, [next]: [] }));
      setFlashTone(null);
      setFlashBeats(2);
      setFlash(ids);
    }
    setView(next);
  };
  useEffect(() => {
    setShown(Math.max(PAGE_SIZE, flashNeed.current));
    flashNeed.current = 0;
  }, [view, rangeKey]);
  useEffect(() => {
    if (!flash.length) return undefined;
    // If the flashed row sits past the rows currently shown (a bucket card
    // returning to a late date slot, say), page out far enough to include
    // it, then scroll once it has painted.
    // On the Recoup view the table list is the whole ledger, which would
    // page the buckets out to a ledger index; look only in the buckets there.
    const lists = view === 'recoup' ? [recoupOpen, recovered] : [viewRows];
    const indexOf = (id) =>
      Math.max(...lists.map((l) => l.findIndex((r) => r.commissionId === id)));
    const maxIdx = Math.max(...flash.map(indexOf));
    if (maxIdx >= shown) {
      setShown(Math.ceil((maxIdx + 1) / PAGE_SIZE) * PAGE_SIZE);
    }
    // Several rows may have moved (a run of Mark paids): scroll to the
    // TOP-MOST of them, so everything that pulses is at or below the fold.
    const topId = [...flash].sort((a, b) => indexOf(a) - indexOf(b))[0];
    requestAnimationFrame(() => {
      const first = document.querySelector(`[data-flash-id="${topId}"]`);
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // Two 0.85s pulses, plus a beat.
    const t = setTimeout(() => setFlash([]), flashBeats * 850 + 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash]);
  const flashCls = (id) => {
    if (!flash.includes(id)) return '';
    const tone =
      flashTone === 'rose'
        ? 'envoy-row-flash-rose'
        : flashTone === 'emerald'
          ? 'envoy-row-flash-emerald'
          : 'envoy-row-flash';
    return flashBeats === 3 ? `${tone} envoy-flash-x3` : tone;
  };
  const movedCount = (v) => (pending[v] || []).length;

  const runAction = async () => {
    if (!action) return;
    const args = { commissionId: action.commissionId, leg: action.leg };
    try {
      let dest = view;
      if (action.mode === 'undo') {
        await unmarkPaid.mutateAsync(args);
        dest = 'owed';
      } else if (action.mode === 'recouped') {
        await markRecouped.mutateAsync({
          commissionId: action.commissionId,
          reference: reference.trim() || undefined,
        });
        dest = 'recoup';
      } else if (action.mode === 'undoRecouped') {
        await unmarkRecouped.mutateAsync({ commissionId: action.commissionId });
        dest = 'recoup';
      } else {
        await markPaid.mutateAsync({
          ...args,
          reference: reference.trim() || undefined,
        });
        dest = 'paid';
      }
      if (dest === view) {
        // The row stays in this view (Recoup: one bucket to the other) and
        // pulses where it lands, in its real order; the flash effect scrolls
        // to it (Nic 2026-09-02). Recovered = emerald, back to To get back =
        // rose.
        setFlashTone(
          action.mode === 'recouped'
            ? 'emerald'
            : action.mode === 'undoRecouped'
              ? 'rose'
              : null,
        );
        setFlashBeats(2);
        setFlash([action.commissionId]);
      } else {
        setPending((p) => ({
          ...p,
          [dest]: [...new Set([...(p[dest] || []), action.commissionId])],
        }));
      }
      setAction(null);
      setReference('');
    } catch {
      // Mutation state carries the failure; keep the modal open for retry.
    }
  };

  // Whichever mutation the open modal drives, surface its failure in the
  // modal (an undo that fails must not just sit there silently).
  const mutations = [markPaid, unmarkPaid, markRecouped, unmarkRecouped];
  const failed = mutations.find((m) => m.isError);
  const actionError = failed
    ? failed.error?.response?.data?.msg || 'Could not record it. Try again.'
    : null;
  const closeAction = () => {
    mutations.forEach((m) => m.reset());
    setAction(null);
    setReference('');
  };
  const errorLine = actionError ? (
    <div className="text-xs text-red-600">{actionError}</div>
  ) : null;

  const legActions = (row, legKey) => {
    if (!canManage) return null;
    const leg = row[legKey];
    if (leg.status === 'owed') {
      return (
        <ActionPill
          status="owed"
          action="Mark paid"
          onClick={() =>
            setAction({
              commissionId: row.commissionId,
              leg: legKey,
              mode: 'pay',
              name: row.clientName,
              amount: leg.amount,
            })
          }
        />
      );
    }
    if (leg.status === 'paid') {
      return (
        <button
          type="button"
          onClick={() =>
            setAction({
              commissionId: row.commissionId,
              leg: legKey,
              mode: 'undo',
              name: row.clientName,
              amount: leg.amount,
            })
          }
          className="inline-flex items-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          title="Undo mark paid"
          aria-label="Undo mark paid"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </button>
      );
    }
    if (legKey === 'sa' && leg.status === 'recouped') {
      return (
        <button
          type="button"
          onClick={() =>
            setAction({
              commissionId: row.commissionId,
              leg: legKey,
              mode: 'undoRecouped',
              name: row.clientName,
              amount: leg.amount,
            })
          }
          className="inline-flex items-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          title="Undo mark recovered"
          aria-label="Undo mark recovered"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </button>
      );
    }
    return null;
  };

  // One line, always: pill, a short muted detail that truncates, and the
  // undo pinned right. A Recoup pill is a LINK to the Recoup view (the
  // action lives there); no detail text, since recoup always means "went
  // to DEAD unpaid" (Nic 2026-09-02).
  const legCell = (row, legKey) => {
    const leg = row[legKey];
    // Recouped carries no detail either: the recovered date lives in the
    // Recovered bucket (Nic 2026-09-02).
    // A pending $250 (QA 2026-10-26): they paid us before VA.gov was
    // confirmed, so it's not owed yet.
    const detail =
      leg.status === 'void'
        ? leg.voidReason
        : legKey === 'sa' && leg.status === 'pending'
          ? 'Not owed until VA.gov is confirmed'
          : null;
    const title = undefined;
    // Paid to Envoy: the date, then the reference on a dimmer second line,
    // behind a receipt icon (Nic 2026-09-02).
    const paidTip =
      leg.status === 'paid' ? (
        <HoverTip
          ariaLabel={`Paid to Envoy ${fmtDate(leg.paidAt)}`}
          label={
            <span className="block">
              <span className="block">Paid to Envoy {fmtDate(leg.paidAt)}</span>
              {leg.paymentReference ? (
                <span className="block text-slate-300">{leg.paymentReference}</span>
              ) : null}
            </span>
          }
        >
          <Receipt size={13} strokeWidth={1.75} />
        </HoverTip>
      ) : null;
    const action =
      leg.status === 'recoup' || leg.status === 'recouped'
        ? null
        : legActions(row, legKey);
    // Layout (Nic 2026-09-02): ONE icon at most, in a fixed-width slot on
    // the LEFT so every pill in a column lines up: the receipt on a paid
    // leg, nothing otherwise. (The client's invoice date used to ride here
    // for the $450; dropped as one icon too many.)
    const leftSlot = (
      <span className="inline-flex w-4 shrink-0 items-center justify-center">
        {paidTip}
      </span>
    );
    const rightSlot = (
      <span className="inline-flex w-4 shrink-0 items-center justify-center">
        {action}
      </span>
    );
    if (leg.status === 'owed' && action) {
      return (
        <div className="flex items-center justify-center gap-2">
          {leftSlot}
          {action}
          <span className="inline-flex w-4 shrink-0" />
        </div>
      );
    }
    // Symmetric slots (icon left, undo right, both fixed width) so the pill
    // itself sits dead center whether or not either side is filled.
    return (
      <div className="flex items-center justify-center gap-2">
        {leftSlot}
        {legKey === 'sa' && (leg.status === 'recoup' || leg.status === 'recouped') ? (
          // A link into the Recoup view that lands on this client's card
          // and pulses it three times (Nic 2026-09-02).
          <button
            type="button"
            onClick={() => jumpToRecoup(row)}
            title="Open this client on the Recoup view"
            className={cn(
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2',
              leg.status === 'recoup'
                ? 'bg-red-50 text-red-600 hover:bg-red-100 focus-visible:ring-red-300'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200 focus-visible:ring-slate-300',
            )}
          >
            <ArrowUpRight size={12} strokeWidth={2.5} />
            {leg.status === 'recoup' ? 'Recoup' : 'Recouped'}
          </button>
        ) : (
          <LegPill status={leg.status} />
        )}
        {detail ? (
          <span
            className="min-w-0 truncate text-[11px] text-slate-400"
            title={title}
          >
            {detail}
          </span>
        ) : null}
        {rightSlot}
      </div>
    );
  };

  // Client name + Envoy id, plus the tombstone for a client who went to DEAD
  // and came back (meta.history). The name truncates on its own so the
  // tooltip is never clipped by an overflow-hidden cell.
  const clientCell = (row, { idOnLgOnly = false } = {}) => (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="min-w-0 truncate">
        {row.clientName || 'Unknown client'}
        {row.leadExternalId ? (
          <span
            className={cn(
              'ml-1.5 text-[11px] font-normal text-slate-400',
              idOnLgOnly ? 'hidden lg:inline' : '',
            )}
          >
            {row.leadExternalId}
          </span>
        ) : null}
      </span>
      {vaGovMarkFor(row)}
      {tombstoneFor(row)}
    </div>
  );

  // Recoup view = two BUCKETS (Nic 2026-09-02): a rose one holding the
  // $250s still in Envoy's hands, an emerald one holding what came back.
  // Each is a white area inside a thick dashed border, its dollar total up
  // top and one white card per client inside (name, the one date that
  // matters, the action).
  // Cards sit in their real order, pulsing where they land (Nic
  // 2026-09-02: no pinning, no reshuffle when the pulse ends); the flash
  // effect pages the list out far enough to show a card that landed past
  // the first page.
  const bucketRows = (rows) => rows.slice(0, shown);
  // The divider sits between the last row signed under the VA.gov rule and
  // the first one signed before it (the table is newest first).
  const underRule = (row) => Boolean(row.vaGov);
  const ruleStartsAbove = (rows, i) =>
    Boolean(data?.vaGovRuleFrom) && i > 0 && underRule(rows[i - 1]) && !underRule(rows[i]);
  const bucketTotal = (rows) =>
    rows.reduce((s, r) => s + (Number(r.sa?.amount) || 0), 0);
  const recoverButton = (row) => (
    <button
      type="button"
      onClick={() =>
        setAction({
          commissionId: row.commissionId,
          leg: 'sa',
          mode: 'recouped',
          name: row.clientName,
          amount: row.sa.amount,
        })
      }
      className="rounded-lg bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
    >
      Mark recovered
    </button>
  );
  const bucket = ({ title, tone, rows, dateOf, dateWord, empty, actionFor }) => {
    const rose = tone === 'rose';
    return (
      <div
        className={cn(
          'rounded-2xl border-2 border-dashed p-4',
          rose ? 'border-rose-200' : 'border-emerald-200',
        )}
      >
        <div className="mb-3 flex items-end justify-between px-1">
          <div>
            <div
              className={cn(
                'text-[11px] font-semibold uppercase tracking-wider',
                rose ? 'text-rose-600' : 'text-emerald-700',
              )}
            >
              {title}
            </div>
            <div className="mt-0.5 text-2xl font-semibold tabular-nums leading-none tracking-tight text-slate-900">
              {money(bucketTotal(rows))}
            </div>
          </div>
          <div className="text-xs tabular-nums text-slate-500">
            {rows.length} {rows.length === 1 ? 'client' : 'clients'}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="rounded-xl bg-slate-50 py-8 text-center text-sm text-slate-400">
            {empty}
          </div>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {bucketRows(rows).map((row) => (
              <li
                key={row.commissionId}
                data-flash-id={row.commissionId}
                className={`relative flex items-center gap-3 rounded-xl bg-white px-3.5 py-2.5 ${CARD_SHADOW}`}
              >
                {/* The pulse is its own overlay, mounted only while the card
                    is flashing: a card that just changed buckets is moved in
                    the DOM in the same commit, which can cancel an animation
                    set on the card itself. */}
                {flash.includes(row.commissionId) ? (
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-0 rounded-xl ${flashCls(row.commissionId)}`}
                  />
                ) : null}
                <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">
                  {clientCell(row, { idOnLgOnly: true })}
                </span>
                <span className="shrink-0 whitespace-nowrap text-[11px] text-slate-400">
                  {dateWord}{' '}
                  <b className="font-medium tabular-nums text-slate-600">
                    {fmtDate(dateOf(row))}
                  </b>
                </span>
                {canManage ? <span className="shrink-0">{actionFor(row)}</span> : null}
              </li>
            ))}
          </ul>
        )}
        {showMore(rows.length)}
      </div>
    );
  };

  const showMore = (total) =>
    total > shown ? (
      <div className="mt-3 flex justify-center">
        <button
          type="button"
          onClick={() => setShown((n) => n + PAGE_SIZE)}
          className={`rounded-xl bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 ${CARD_SHADOW}`}
        >
          Show more
          <span className="ml-1.5 font-normal text-slate-400">
            {total - shown} left
          </span>
        </button>
      </div>
    ) : null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-56 rounded-2xl" />
        {/* Mirrors the live layout: eyebrow, then four short tiles. */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-40" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={i} className={`${TILE_H} rounded-2xl`} />
            ))}
          </div>
        </div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }
  if (!data?.partner) {
    return (
      <Card className="py-14 text-center">
        <span className="text-sm text-slate-400">
          No Envoy partner is configured in this environment.
        </span>
      </Card>
    );
  }

  // Settled = paid and Envoy's to keep, over everything on the board
  // (kept + owed + in flight back to us), so the number matches the ring.
  const boardTotal =
    summary.keptByPartnerAmount + summary.owedNowAmount + summary.recoupAmount;
  const settledPct =
    boardTotal > 0
      ? Math.round((summary.keptByPartnerAmount / boardTotal) * 100)
      : null;

  return (
    <>
      {/* Hero: the money scorecard (scoped by the date window, like the table).
          Click flips to the plain-English explainer. */}
      <FlipCard
        className="mb-4"
        front={
          <HeroCard hint={<FlipHint />}>
            <div className="flex flex-col items-center gap-6 text-center lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-8 lg:text-left">
              <div className="lg:justify-self-start">
                <h2 className="mb-0 text-2xl font-bold text-white">Envoy Money</h2>
                <div className="mt-1 text-sm leading-relaxed text-slate-500">
                  what we owe for their leads
                  <br />
                  {monthLabel}
                </div>
              </div>
              <div className="lg:justify-self-center">
                <Donut
                  segments={[
                    { key: 'paid', value: summary.keptByPartnerAmount, color: COLORS.paid },
                    { key: 'owed', value: summary.owedNowAmount, color: COLORS.owed },
                    { key: 'recoup', value: summary.recoupAmount, color: COLORS.recoup },
                  ]}
                  track={COLORS.restDark}
                  size={150}
                  stroke={14}
                >
                  <span className="text-3xl font-bold tabular-nums text-white">
                    {settledPct == null ? '-' : `${settledPct}%`}
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    settled
                  </span>
                </Donut>
              </div>
              <div className="lg:justify-self-end lg:pr-6 lg:text-right">
                <div className="whitespace-nowrap text-4xl font-bold leading-none tabular-nums text-white sm:text-5xl xl:text-6xl">
                  {money(owedNowAmount)}
                </div>
                <div className="mt-2.5 text-sm font-semibold uppercase tracking-wider text-slate-400">
                  owed to Envoy now
                </div>
                <div className="mt-2 text-sm tabular-nums text-slate-500">
                  {money(summary.paidToPartnerAmount)} paid to date
                  {summary.recoupAmount > 0 ? (
                    <>
                      {' '}·{' '}
                      <span className="text-red-400">
                        {money(summary.recoupAmount)} to recoup
                      </span>
                    </>
                  ) : null}
                </div>
                {/* Owed outside the month picked (Nic 2026-11-03): never
                    invisible, never mixed into the month. */}
                {owedElsewhere.count > 0 ? (
                  <div className="mt-1.5 text-xs tabular-nums text-amber-300/90">
                    {owedElsewhere.count} client{owedElsewhere.count === 1 ? '' : 's'} from other months still owed · {money(owedElsewhere.amount)}
                  </div>
                ) : null}
              </div>
            </div>
          </HeroCard>
        }
        back={
          <HeroCard hint={<FlipHint close />}>
            <div className="flex h-full flex-col">
              <h2 className="mb-0 text-base font-bold text-white">Envoy Money</h2>
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-center">
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                  <LegendChip
                    color={COLORS.paid}
                    value={money(summary.keptByPartnerAmount)}
                    label="paid, theirs to keep"
                  />
                  <LegendChip
                    color={COLORS.owed}
                    value={money(summary.owedNowAmount)}
                    label="owed now"
                  />
                  <LegendChip
                    color={COLORS.recoup}
                    value={money(summary.recoupAmount)}
                    label="paid, needs recouped"
                  />
                </div>
                <div className="text-sm tabular-nums text-slate-400">
                  <b className="font-semibold text-slate-300">$250</b> the
                  moment an SA is signed ·{' '}
                  <b className="font-semibold text-slate-300">$450</b> when the
                  client's first invoice is paid
                </div>
                {data.vaGovRuleFrom ? (
                  <div className="text-sm text-violet-300">
                    SAs signed from {fmtDate(data.vaGovRuleFrom)} on: the $250 from the
                    day their onboarding specialist confirms VA.gov access
                  </div>
                ) : null}
                <div className="text-sm text-slate-500">
                  red = paid to Envoy for a client who went to DEAD before ever
                  paying us, so we need it back
                </div>
              </div>
            </div>
          </HeroCard>
        }
      />

      {/* KPI tiles: the same window as the hero and ledger (2026-10-25:
          with "All time" gone, the tiles follow the picker too). The eyebrow
          mirrors the ledger card's "LEDGER  Jul 2026 · 60 clients" header. */}
      <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Snapshot
        <span className="ml-2 normal-case tracking-normal text-slate-300">{monthLabel}</span>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          flipKey={rangeKey}
          icon={ScrollText}
          tint="bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-sm shadow-emerald-500/25"
          accent="text-emerald-600"
          label="Signed SAs"
          value={tiles.signedSas}
          info={`${n(tiles.signedSas)} of the ${n(tiles.totalLeads)} leads Envoy has ever sent became clients who signed an SA, $250 each. Void means they went to DEAD before paying us and we never paid Envoy. Waiting on VA.gov means they signed under the VA.gov rule and nobody has confirmed their access yet.`}
          stats={[
            { label: 'owed', raw: tiles.saOwed.count, value: n(tiles.saOwed.count), color: TILE_COLORS.owed },
            { label: 'paid', raw: tiles.saPaid?.count, value: n(tiles.saPaid?.count), color: TILE_COLORS.paid },
            { label: 'void', raw: tiles.saVoid?.count, value: n(tiles.saVoid?.count), color: TILE_COLORS.void },
            { label: 'needs recouped', raw: tiles.recoup.count, value: n(tiles.recoup.count), color: TILE_COLORS.recoup },
            { label: 'recovered', raw: tiles.recouped.count, value: n(tiles.recouped.count), color: TILE_COLORS.recovered },
          ]}
        />
        <KpiCard
          flipKey={rangeKey}
          icon={UserMinus}
          tint="bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-sm shadow-orange-500/25"
          accent="text-orange-500"
          label="Drop offs"
          value={tiles.withdrawn.unpaid}
          info={`${n(tiles.withdrawn.unpaid)} clients went to DEAD before ever paying us, so their $250 was not earned: void if we had not paid Envoy yet, needs recouped if we had, recovered once Envoy returned it. Clients who went to DEAD after paying us are not counted; Envoy keeps that $250.`}
          stats={[
            { label: 'void', raw: tiles.saVoid?.count, value: n(tiles.saVoid?.count), color: TILE_COLORS.void },
            { label: 'needs recouped', raw: tiles.recoup.count, value: n(tiles.recoup.count), color: TILE_COLORS.recoup },
            { label: 'recovered', raw: tiles.recouped.count, value: n(tiles.recouped.count), color: TILE_COLORS.recovered },
          ]}
        />
        <KpiCard
          flipKey={rangeKey}
          icon={HandCoins}
          tint={
            tiles.recoup.count > 0
              ? 'bg-gradient-to-br from-rose-400 to-rose-500 text-white shadow-sm shadow-rose-500/25'
              : 'bg-gradient-to-br from-slate-300 to-slate-400 text-white'
          }
          accent={tiles.recoup.count > 0 ? 'text-rose-500' : 'text-slate-500'}
          label="Recoup"
          value={tiles.recoup.count}
          info={`${n(tiles.recoup.count)} x $250 paid to Envoy for clients who then went to DEAD before paying us: ${money(tiles.recoup.amount)} to get back. ${n(tiles.recouped.count)} recovered so far (${money(tiles.recouped.amount)}).`}
          stats={[
            { label: 'needs recouped', raw: tiles.recoup.amount, value: money(tiles.recoup.amount), color: TILE_COLORS.recoup },
            { label: 'recovered', raw: tiles.recouped.amount, value: money(tiles.recouped.amount), color: TILE_COLORS.recovered },
          ]}
        />
        <KpiCard
          flipKey={rangeKey}
          icon={CircleDollarSign}
          tint="bg-gradient-to-br from-sky-400 to-sky-500 text-white shadow-sm shadow-sky-500/25"
          accent="text-sky-600"
          label="Clients who paid"
          value={tiles.clientsPaid}
          info={`${n(tiles.clientsPaid)} Envoy clients have paid their first invoice in full, $450 each.`}
          stats={[
            { label: 'owed', raw: tiles.payOwed.count, value: n(tiles.payOwed.count), color: TILE_COLORS.owed },
            { label: 'paid', raw: tiles.payPaid?.count, value: n(tiles.payPaid?.count), color: TILE_COLORS.paid },
          ]}
        />
      </div>

      {/* Ledger: one card, four views. Title left, tabs centered, money
          pills right. */}
      <Card
        title="Ledger"
        caption={`${monthLabel} · ${ledger.length} ${ledger.length === 1 ? 'client' : 'clients'}`}
        center={
          <ViewTabs
            value={view}
            onChange={changeView}
            options={[
              { key: 'owed', label: 'Owed', count: owedRows.length, moved: movedCount('owed') },
              { key: 'paid', label: 'Paid', count: paidRows.length, moved: movedCount('paid') },
              {
                key: 'recoup',
                label: 'Recoup',
                count: recoupOpen.length,
                alert: recoupOpen.length > 0,
                moved: movedCount('recoup'),
              },
              { key: 'all', label: 'All', count: ledger.length },
            ]}
          />
        }
        right={
          <>
            {view !== 'recoup' && owedNowAmount > 0 ? (
              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600">
                {money(owedNowAmount)} owed
              </span>
            ) : null}
            {view !== 'recoup' && summary.paidToPartnerAmount > 0 ? (
              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
                {money(summary.paidToPartnerAmount)} paid
              </span>
            ) : null}
            {view === 'recoup' && summary.recoupAmount > 0 ? (
              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                {money(summary.recoupAmount)} to get back
              </span>
            ) : null}
          </>
        }
      >
        {needsReview.length > 0 && view === 'all' ? (
          <div className="mb-3 rounded-xl bg-amber-50 px-4 py-2.5 text-xs leading-5 text-amber-700">
            {needsReview.length} legacy Envoy{' '}
            {needsReview.length === 1 ? 'client has' : 'clients have'} no dated
            SA on file (signed before e-sign tracking):{' '}
            {needsReview.map((u) => u.name).join(', ')}. Reconcile these by
            hand.
          </div>
        ) : null}

        {view === 'recoup' ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {bucket({
              title: 'To get back',
              tone: 'rose',
              rows: recoupOpen,
              dateOf: (row) => row.meta?.withdrawal?.at || row.sa.recoupFlaggedAt,
              dateWord: 'to DEAD',
              empty: 'Nothing owed back to us.',
              actionFor: recoverButton,
            })}
            {bucket({
              title: 'Recovered',
              tone: 'emerald',
              rows: recovered,
              dateOf: (row) => row.sa.recoupedAt,
              dateWord: 'back',
              empty: 'Nothing recovered yet.',
              actionFor: (row) => legActions(row, 'sa'),
            })}
          </div>
        ) : viewRows.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">
            {view === 'owed'
              ? 'Nothing owed to Envoy right now.'
              : view === 'paid'
                ? 'Nothing paid to Envoy yet.'
                : range
                  ? `No SAs were signed ${monthLabel}.`
                  : 'No signed Envoy clients yet.'}
          </div>
        ) : (
          <>
            {/* table-fixed so the columns always share the card width; long
                content truncates instead of forcing a page scrollbar */}
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[14%]" />
                <col className="w-[28%]" />
                <col className="w-[28%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-100">
                  <Th className="pl-0">Client</Th>
                  <Th className="text-center">Signed SA</Th>
                  <Th className="text-center">$250 · signed SA</Th>
                  <Th className="text-center">$450 · invoice paid</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.map((row, i) => (
                  <React.Fragment key={row.commissionId}>
                    {ruleStartsAbove(visibleRows, i) ? (
                      // Newest first: the rows above signed under the VA.gov rule.
                      <tr aria-label="VA.gov rule starts">
                        <td colSpan={4} className="px-0 py-2">
                          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-violet-500">
                            <span className="h-px flex-1 bg-violet-200" />
                            <Landmark size={12} strokeWidth={2.25} aria-hidden />
                            VA.gov rule starts {fmtDate(data.vaGovRuleFrom)}
                            <span className="normal-case tracking-normal font-normal text-slate-400">
                              · above: the $250 from the day Onboarding confirms VA.gov
                            </span>
                            <span className="h-px flex-1 bg-violet-200" />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    <tr
                      data-flash-id={row.commissionId}
                      className={`hover:bg-slate-50/60 ${flashCls(row.commissionId)}`}
                    >
                      <td className="py-2.5 pr-3 font-medium text-slate-800">
                        {clientCell(row)}
                      </td>
                      <td className="truncate px-3 py-2.5 tabular-nums text-slate-500">
                        {fmtDate(row.saSignedAt)}
                      </td>
                      <td className="px-3 py-2.5">{legCell(row, 'sa')}</td>
                      <td className="px-3 py-2.5">{legCell(row, 'pay')}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {showMore(viewRows.length)}
          </>
        )}
      </Card>

      {action ? (
        <ConfirmModal
          title={
            action.mode === 'undo'
              ? 'Undo mark paid'
              : action.mode === 'undoRecouped'
                ? 'Undo mark recovered'
                : action.mode === 'recouped'
                  ? 'Mark recovered'
                  : 'Mark paid'
          }
          body={
            action.mode === 'undo' ? (
              <div className="space-y-3">
                <span>
                  Put the {money(action.amount)} for{' '}
                  <strong>{action.name || 'this client'}</strong> back to owed?
                </span>
                {errorLine}
              </div>
            ) : action.mode === 'undoRecouped' ? (
              <div className="space-y-3">
                <span>
                  Put the {money(action.amount)} for{' '}
                  <strong>{action.name || 'this client'}</strong> back on the
                  recoup list?
                </span>
                {errorLine}
              </div>
            ) : (
              <div className="space-y-3">
                <span>
                  {money(action.amount)}{' '}
                  {action.mode === 'recouped' ? 'back from Envoy' : 'to Envoy'} for{' '}
                  <strong>{action.name || 'this client'}</strong>.
                </span>
                <div>
                  <label
                    htmlFor="envoy-pay-ref"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    {action.mode === 'recouped'
                      ? 'Reference (optional)'
                      : 'Payment reference (optional)'}
                  </label>
                  <input
                    id="envoy-pay-ref"
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder={
                      action.mode === 'recouped'
                        ? 'Credit memo, check number...'
                        : 'Check number, ACH id...'
                    }
                    className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
                  />
                </div>
                {errorLine}
              </div>
            )
          }
          confirmLabel={
            action.mode === 'undo' || action.mode === 'undoRecouped'
              ? 'Undo'
              : action.mode === 'recouped'
                ? 'Mark recovered'
                : 'Mark paid'
          }
          destructive={action.mode === 'undo' || action.mode === 'undoRecouped'}
          // The page's dark button, not the theme blue (Nic 2026-09-02).
          confirmClassName={
            action.mode === 'undo' || action.mode === 'undoRecouped'
              ? undefined
              : 'bg-slate-900 text-white hover:bg-slate-800'
          }
          onClose={closeAction}
          onConfirm={runAction}
          isSaving={
            markPaid.isLoading ||
            unmarkPaid.isLoading ||
            markRecouped.isLoading ||
            unmarkRecouped.isLoading
          }
        />
      ) : null}
    </>
  );
};

// ---------------------------------------------------------------------------
// Connections tab
// ---------------------------------------------------------------------------

// Color a segment cell only when it meaningfully beats or trails the
// all-leads baseline; everything else stays quiet slate.
const CELL_DELTA = 8; // percentage points
const rateTone = (segPct, basePct, goodWhenUp) => {
  if (segPct == null || basePct == null) return 'text-slate-500';
  const diff = segPct - basePct;
  if (Math.abs(diff) < CELL_DELTA) return 'text-slate-500';
  const good = goodWhenUp ? diff > 0 : diff < 0;
  return good ? 'font-semibold text-emerald-600' : 'font-semibold text-red-500';
};
const daysTone = (segDays, baseDays) => {
  if (segDays == null || baseDays == null || baseDays <= 0)
    return 'text-slate-500';
  if (segDays <= baseDays / 1.5) return 'font-semibold text-emerald-600';
  if (segDays >= baseDays * 1.5) return 'font-semibold text-red-500';
  return 'text-slate-500';
};

const pctCell = (v) => (v == null ? '-' : `${v}%`);
const daysCell = (v) => (v == null ? '-' : `${v}d`);

const BigStat = ({ value, label, sub, colorClass }) => (
  <div className="flex flex-col items-center">
    <span className={`text-5xl font-semibold leading-none tabular-nums ${colorClass}`}>
      {value}
    </span>
    <span className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
      {label}
    </span>
    {sub ? <span className="mt-0.5 text-[11px] text-slate-300">{sub}</span> : null}
  </div>
);

// Fixed-layout table sized to the card: the label column gets the leftover
// width, the five numeric columns split the rest evenly. No horizontal
// scrolling, ever - everything fits the tile (Nic 2026-08-30).
const AttributeCard = ({ attribute }) => {
  const { label, overall, segments } = attribute;
  if (!segments.length) return null;
  const numCell = 'py-2 pl-1 text-right tabular-nums';
  return (
    <Card title={label} caption="vs all Envoy leads">
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-[26%]" />
          <col className="w-[13%]" />
          <col className="w-[13%]" />
          <col className="w-[13%]" />
          <col className="w-[17%]" />
          <col className="w-[18%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-slate-100">
            <Th className="pl-0" />
            <Th className="pl-1 pr-0 text-right">Leads</Th>
            <Th className="pl-1 pr-0 text-right">Sign</Th>
            <Th className="pl-1 pr-0 text-right">Drop</Th>
            <Th className="pl-1 pr-0 text-right">Days</Th>
            <Th className="pl-1 pr-0 text-right">Withdraw</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {segments.map((s) => (
            <tr key={s.value}>
              <td
                className="truncate py-2 pr-1 font-medium text-slate-700"
                title={s.value}
              >
                {s.value}
              </td>
              <td className={`${numCell} text-slate-500`}>{s.received}</td>
              <td className={`${numCell} ${rateTone(s.signPct, overall.signPct, true)}`}>
                {pctCell(s.signPct)}
              </td>
              <td className={`${numCell} ${rateTone(s.dropPct, overall.dropPct, false)}`}>
                {pctCell(s.dropPct)}
              </td>
              <td className={`${numCell} ${daysTone(s.medianDaysToSign, overall.medianDaysToSign)}`}>
                {daysCell(s.medianDaysToSign)}
              </td>
              <td className={`${numCell} ${rateTone(s.withdrawPct, overall.withdrawPct, false)}`}>
                {pctCell(s.withdrawPct)}
              </td>
            </tr>
          ))}
          <tr>
            <td className="py-2 pr-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              All leads
            </td>
            <td className={`${numCell} text-slate-400`}>{overall.received}</td>
            <td className={`${numCell} text-slate-400`}>
              {pctCell(overall.signPct)}
            </td>
            <td className={`${numCell} text-slate-400`}>
              {pctCell(overall.dropPct)}
            </td>
            <td className={`${numCell} text-slate-400`}>
              {daysCell(overall.medianDaysToSign)}
            </td>
            <td className={`${numCell} text-slate-400`}>
              {pctCell(overall.withdrawPct)}
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

const ConnectionsTab = () => {
  const { data, isLoading } = useEnvoyConnections();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-56" />
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }
  if (!data?.partner) {
    return (
      <Card className="py-14 text-center">
        <span className="text-sm text-slate-400">
          No Envoy partner is configured in this environment.
        </span>
      </Card>
    );
  }

  const t = data.totals;
  const attributes = (data.attributes || []).filter((a) => a.segments.length);

  return (
    <>
      {/* Hero: the funnel scorecard. Click flips to the explainer. */}
      <FlipCard
        className="mb-4"
        front={
          <HeroCard hint={<FlipHint />}>
            <div className="flex flex-col items-center gap-6 text-center lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-8 lg:text-left">
              <div className="lg:justify-self-start">
                <h2 className="mb-0 text-2xl font-bold text-white">Connections</h2>
                <div className="mt-1 text-sm leading-relaxed text-slate-500">
                  what Envoy's own lead data predicts
                  <br />
                  {t.withProfile} of {t.received} leads carry a profile (
                  {t.profilePct == null ? 0 : t.profilePct}%)
                </div>
              </div>
              <div className="lg:justify-self-center">
                <Donut
                  segments={[
                    { key: 'signed', value: t.signed, color: COLORS.paid },
                    {
                      key: 'accountsOnly',
                      value: Math.max(0, t.accounts - t.signed),
                      color: COLORS.window,
                    },
                    { key: 'dropped', value: t.dropped, color: '#EF4444' },
                    {
                      key: 'rest',
                      value: Math.max(0, t.received - t.accounts - t.dropped),
                      color: 'rgba(255,255,255,0.12)',
                    },
                  ]}
                  track={COLORS.restDark}
                  size={150}
                  stroke={14}
                >
                  <span className="text-3xl font-bold tabular-nums text-white">
                    {t.signPct == null ? '-' : `${t.signPct}%`}
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    sign an SA
                  </span>
                </Donut>
              </div>
              <div className="lg:justify-self-end lg:pr-6 lg:text-right">
                <div className="whitespace-nowrap text-4xl font-bold leading-none tabular-nums text-white sm:text-5xl xl:text-6xl">
                  {t.medianDaysToSign == null ? '-' : t.medianDaysToSign}
                  <span className="text-3xl font-semibold text-slate-500"> days</span>
                </div>
                <div className="mt-2.5 text-sm font-semibold uppercase tracking-wider text-slate-400">
                  median lead to signed SA
                </div>
              </div>
            </div>
          </HeroCard>
        }
        back={
          <HeroCard hint={<FlipHint close />}>
            <div className="flex h-full flex-col">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="mb-0 text-base font-bold text-white">Connections</h2>
                <span className="text-sm tabular-nums text-slate-400">
                  <b className="font-semibold text-white">{t.received}</b> Envoy
                  leads all time
                </span>
              </div>
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-center">
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                  <LegendChip
                    color={COLORS.paid}
                    value={t.signed}
                    label="signed an SA"
                  />
                  <LegendChip
                    color={COLORS.window}
                    value={Math.max(0, t.accounts - t.signed)}
                    label="account, no SA yet"
                  />
                  <LegendChip color="#EF4444" value={t.dropped} label="dropped" />
                </div>
                <div className="text-sm tabular-nums text-slate-400">
                  all{' '}
                  <b className="font-semibold text-slate-300">{t.received}</b>{' '}
                  Envoy leads by outcome · the gray part of the ring ={' '}
                  <b className="font-semibold text-slate-300">
                    {Math.max(0, t.received - t.accounts - t.dropped)}
                  </b>{' '}
                  leads still in the pipeline
                </div>
                <div className="text-sm text-slate-500">
                  median = days from a lead arriving to its SA being signed
                </div>
              </div>
            </div>
          </HeroCard>
        }
      />

      {/* Big funnel numbers */}
      <Card className="mb-4">
        <div className="grid grid-cols-2 gap-y-6 py-2 sm:grid-cols-4">
          <BigStat
            value={pctCell(t.accountPct)}
            label="Create accounts"
            sub={`${t.accounts} of ${t.received}`}
            colorClass="text-sky-500"
          />
          <BigStat
            value={pctCell(t.signPct)}
            label="Sign an SA"
            sub={`${t.signed} of ${t.received}`}
            colorClass="text-emerald-500"
          />
          <BigStat
            value={pctCell(t.dropPct)}
            label="Get dropped"
            sub={`${t.dropped} of ${t.received}`}
            colorClass="text-amber-500"
          />
          <BigStat
            value={pctCell(t.withdrawPct)}
            label="Withdraw after signing"
            sub={`${t.withdrawn} of ${t.signed} signers`}
            colorClass="text-red-500"
          />
        </div>
      </Card>

      {/* Per-attribute breakdowns */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {attributes.map((a) => (
          <AttributeCard key={a.key} attribute={a} />
        ))}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Page shell: title left, tab bar center (Lead Metrics recipe)
// ---------------------------------------------------------------------------

// Connections is built (tab, endpoint, tests) but not ready to show (Nic
// 2026-09-02). Flip this to bring the tab switch back; until then the page
// is the Ledger only.
const SHOW_CONNECTIONS = false;

const EnvoyDashboardPage = () => {
  const { hasPermission } = useUser();
  const canManage = hasPermission('envoy_finance:manage');
  const [tab, setTab] = useState('ledger');
  // This year by default (2026-10-25: no "All time").
  const [range, setRange] = useState(() => yearRange(dayjs().year()));
  const showConnections = SHOW_CONNECTIONS && tab === 'connections';

  return (
    <PrivateLayout background="#F5F7FA">
      <div className="w-full">
        {/* Header: 1fr / auto / 1fr grid keeps the tab pill DEAD centered at
            every width; the side columns can shrink (subtitle truncates,
            export drops its label). Below md it stacks, everything centered. */}
        <div className="mb-3 grid grid-cols-1 items-center gap-2 md:grid-cols-[1fr_auto_1fr]">
          <div className="min-w-0">
            <h2 className="mb-0 flex items-center gap-2 whitespace-nowrap text-xl font-bold text-slate-800">
              <Handshake size={19} className="text-slate-400" />
              Envoy
            </h2>
            <div className="truncate text-xs text-slate-400">
              {SHOW_CONNECTIONS
                ? 'The money we owe them, and what their lead data tells us'
                : 'The money we owe them, client by client'}
            </div>
          </div>
          {/* Below md the pill and the controls share one centered row; at
              md+ the wrapper dissolves (contents) and they return to their
              own grid cells. */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:contents">
            {SHOW_CONNECTIONS ? (
              <div
                className={`inline-flex justify-self-center rounded-xl bg-white p-1 ${CARD_SHADOW}`}
              >
                <TabButton active={tab === 'ledger'} onClick={() => setTab('ledger')}>
                  Ledger
                </TabButton>
                <TabButton
                  active={tab === 'connections'}
                  onClick={() => setTab('connections')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Network size={14} strokeWidth={2} />
                    Connections
                  </span>
                </TabButton>
              </div>
            ) : (
              <span className="hidden md:block" />
            )}
            <div className="flex min-w-0 items-center justify-center gap-2 md:justify-end">
              {!showConnections ? (
                <LedgerControls range={range} setRange={setRange} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl pb-10 pt-1">
          {showConnections ? (
            <ConnectionsTab />
          ) : (
            <LedgerTab canManage={canManage} range={range} />
          )}
        </div>
      </div>
    </PrivateLayout>
  );
};

export default EnvoyDashboardPage;
